import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import NavBar from '../../components/common/NavBar';
import Icon from '../../components/common/Icon';
import EventSocial from '../../components/common/EventSocial';
import MeetQrSheet from '../../components/common/MeetQrSheet';
import Countdown from '../../components/elements/Countdown';
import { apiErrorText, apiRequest, formatTime, eventDayLabel, hasEventTime, eventZone,
  parseEventDate, groupSponsorsByType } from '../../utils';

import '../user/PublicProfile.css';
import '../event/EventList.css';
import './PublicEvent.css';

const eventFallback = require('./../../assets/images/fallback/event.png');

// NavBar self-hides for unauth viewers (see NavBar.js).
const Layout = ({ children }) => (
  <Screen>
    <div className="top">{children}</div>
    <div className="bottom">
      <NavBar />
    </div>
  </Screen>
);

const toHHMM = (v) => {
  if (!v) return '';
  const m = String(v).match(/(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!m) return String(v);
  return `${m[1].padStart(2, '0')}:${m[2]}`;
};

// The "add to calendar" link points at the server-served .ics endpoint
// (/api/public-event/<id>/calendar.ics). A real text/calendar URL is the only
// thing iOS Safari / Apple Calendar reliably open — the previous client-side
// Blob + <a download> silently did nothing there.
const eventIcsUrl = (event) => (event && event.id ? `/api/public-event/${event.id}/calendar.ics` : null);

const Page = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [data, setData] = useState(null);
  // Networking QR. ★ This page is the entry point that matters: it carries the event's OWN id,
  // while a Bravos member's session sits on the Season rather than on the Lab they are at.
  const [showMeetQr, setShowMeetQr] = useState(false);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'notfound'
  // Why the event is withheld, when the server is willing to say: 'notInvited' or null.
  const [privateReason, setPrivateReason] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [guest, setGuest] = useState({ first_name: '', last_name: '', email: '' });
  const [showGuest, setShowGuest] = useState(false);
  const [busyInvite, setBusyInvite] = useState(false);
  const [inviteNotice, setInviteNotice] = useState(null);
  // The viewer's own answer to a private company event, kept locally so the buttons flip the
  // moment they are pressed instead of after a reload.
  const [rsvp, setRsvp] = useState(null);
  const [busyRsvp, setBusyRsvp] = useState(false);
  const [rsvpNotice, setRsvpNotice] = useState(null);
  // Surveys this person still owes on this event. Kept in its own state rather than read off
  // `data`, because answering the RSVP updates it without a reload.
  const [pending, setPending] = useState([]);
  // The event visual, opened full-screen over the page.
  const [zoom, setZoom] = useState(false);
  // An admin's OWN seat at this Lab, answered here rather than behind the member home.
  const [busySelf, setBusySelf] = useState(false);
  const [selfDone, setSelfDone] = useState(false);
  const [selfNotice, setSelfNotice] = useState(null);

  useEffect(() => {
    if (!eventId) { setStatus('notfound'); return; }
    let cancelled = false;
    setStatus('loading');
    apiRequest('GET', `/public-event/${eventId}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setRsvp(res.company_event ? res.company_event.my_rsvp : null);
        // A private event answers on its RSVP block, a public one on its own key. Same shape,
        // same rule — the server only fills either for somebody it counts as coming.
        setPending((res.company_event && res.company_event.pending_surveys)
                   || res.my_surveys || []);
        setStatus('ok');
      })
      .catch((err) => {
        if (cancelled) return;
        // A PRIVATE event (one belonging to a single company) needs a login. The person
        // following the link is usually a colleague who is simply signed out, so send them to
        // sign in and come back here — telling them the event does not exist would send them
        // to ask the host why the link is broken.
        if (err && err.message === 'privateEvent') {
          // Two different people hit this. Someone SIGNED OUT is almost always a colleague who
          // followed the invitation from their inbox, so they get a sentence and a sign-in
          // button. Jumping straight to /login was one click fewer and read as a broken link:
          // an event page that vanishes into a login form explains nothing, and the people who
          // are genuinely not invited learn nothing either way.
          // `reason` names the EVENT, never the reader — the server withholds it entirely on a
          // hidden event, so an absent reason must fall back to the neutral sentence.
          setPrivateReason((err.data && err.data.reason) || null);
          setStatus((err.data && err.data.login_required) ? 'signin' : 'private');
          return;
        }
        setStatus('notfound');
      });
    return () => { cancelled = true; };
  }, [eventId, navigate]);

  // Close the full-screen visual on Escape (tapping it closes it too).
  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setZoom(false); };
    window.addEventListener('keydown', onKey);
    // The page behind must not scroll away under the visual.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [zoom]);

  // ---------- LOADING ----------
  if (status === 'loading') {
    return (
      <Layout>
        <div className="pp-page">
          <div className="pp-skeleton pp-skel-hero" />
          <div className="pp-skeleton pp-skel-row" />
          <div className="pp-skeleton pp-skel-row" />
        </div>
      </Layout>
    );
  }

  if (status === 'signin') {
    return (
      <Layout>
        <div className="pp-page">
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="lock" /></div>
            <div className="pp-empty-title">
              {t('privateEventSignIn',
                 { defaultValue: 'This event is for one company. Sign in to see it.' })}
            </div>
            <button
              type="button"
              className="pe-bravos-cta"
              onClick={() => {
                // The platform's own come-back-here convention: Login reads sessionStorage
                // 'redirect' after a successful sign-in. Not a ?next= param — that would be a
                // second mechanism doing the same job.
                sessionStorage.setItem('redirect', `/public-event/${eventId}`);
                navigate('/login');
              }}
            >
              {t('login', { defaultValue: 'Sign in' })}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (status === 'private') {
    return (
      <Layout>
        <div className="pp-page">
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="lock" /></div>
            {/* An invite-only event is not "for one company" — the reader may well BE at that
                company. And they are signed in already, so the old copy's "sign in with your
                work account" was advice they had followed. */}
            <div className="pp-empty-title">
              {privateReason === 'notInvited'
                ? t('inviteOnlyEvent', { defaultValue: 'This is an invite-only event' })
                : t('privateEvent', { defaultValue: 'This event is for one company only' })}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (status === 'notfound' || !data) {
    return (
      <Layout>
        <div className="pp-page">
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="info" /></div>
            <div className="pp-empty-title">{t('eventNotFound', { defaultValue: 'Event not found' })}</div>
          </div>
        </div>
      </Layout>
    );
  }

  const event = data.event;
  const heroBg = event.image_link || event.photo_url || eventFallback;
  const logo = event.photo_url || eventFallback;
  // A Bravos Lab (any Bravos event that isn't the BU's Season/master) shows a square
  // poster with the visual kept clean — no logo/name over it — and the date/time/location
  // chips sitting BELOW the image. The Season/master keeps the wide banner hero.
  const isBravosLab = !!(data.bravos && data.bravos.is_bravos && !data.bravos.is_season);
  // A company's own event gets that same square poster: the picture it uploaded is a photo,
  // not a wide banner, and the hero crop cut the middle out of it.
  const isSquareHero = isBravosLab || !!data.is_company_event;
  // Off the stored UTC value, not a browser-local reading of it — the countdown flipped
  // to "past" the offset early from a zone ahead of UTC.
  const isPreEvent = !!event.date && parseEventDate(event.date) >= new Date();
  const planningEntries = Object.entries(event.planning_by_day || {});
  const hasPlanning = planningEntries.length > 0;
  const sponsors = data.event.sponsors || [];
  const hosts = event.hosts || [];

  // Start–end time chip (from the start/end datetimes). Only when a real HH:MM is present and it
  // isn't a date-only 00:00 value.
  //
  // `date`/`end_date` are stored UTC and shown in the EVENT's zone, not the viewer's — a Dubai
  // Lab at 08:00 read 04:00 before this. The "is a time set?" test stays on the RAW value though:
  // converting a date-only midnight first hands it a fake wall time from the offset.
  const tz = eventZone(event);
  const showStart = hasEventTime(event.date);
  const showEnd = hasEventTime(event.end_date) && toHHMM(event.end_date) !== toHHMM(event.date);
  const timeLabel = showStart
    ? (showEnd
        ? `${formatTime(event.date, tz)}–${formatTime(event.end_date, tz)}`
        : formatTime(event.date, tz))
    : '';
  // A date-only event carries no zone-shiftable time, so eventDayLabel renders it off UTC —
  // that reproduces the calendar day as stored instead of slipping one west of UTC.
  const dateLabel = eventDayLabel(event);

  // Answer a private company event. Goes through the same route the calendar uses, so a yes
  // mints the same comp seat and check-in QR wherever it was pressed.
  const answerRsvp = (status) => {
    if (busyRsvp) return;
    setBusyRsvp(true);
    setRsvpNotice(null);
    apiRequest('POST', `/company/event/${eventId}/rsvp`, { status })
      .then((res) => {
        setRsvp(res.status);
        // ★ The moment somebody says they are coming is the moment to ask. They are looking at
        // us and thinking about the event; a pre-survey sent three days later competes with
        // everything else in an inbox. A "no" answers with an empty list.
        setPending(res.pending_surveys || []);
      })
      // ★ apiRequest puts the route's PROSE on `err.message` whenever it sends one, so
      // `t(err.message)` on a refusal printed the generic "something went wrong" and the person
      // was left with a button that silently did nothing. apiErrorText reads the KEY off the
      // body first — the same line the company calendar shows for the same refusal.
      .catch((err) => setRsvpNotice(apiErrorText(t, err, 'somethingWentWrong')))
      .finally(() => setBusyRsvp(false));
  };

  // Each button says what pressing it would DO, so the pair never reads as two labels for the
  // same state: the answer you gave stands on its own button, and the other one offers the
  // change of mind out loud instead of repeating the neutral "I'm (not) coming".
  let comingLabel = t('imComing');
  if (rsvp === 'going') comingLabel = t('youAreComing');
  else if (rsvp === 'not_going') comingLabel = t('comingAfterAll');
  let notComingLabel = t('imNotComing');
  if (rsvp === 'not_going') notComingLabel = t('youAreNotComing');
  else if (rsvp === 'going') notComingLabel = t('cantMakeItAfterAll');

  // A contact person doesn't book a Lab, they fill it. Three ways, because they are three
  // different situations and one button cannot be all of them:
  //   - the LINK, to drop into a WhatsApp group or a newsletter;
  //   - an EMAIL to one person whose address they have in front of them;
  //   - the roster, when they want to pick several colleagues at once.
  // The first two need a seat in the pool; the roster screen says so itself.
  const sendInviteLink = async () => {
    const link = data.bravos && data.bravos.invite_link;
    if (!link) { navigate('/member-home'); return; }
    // Share a warm, ready-to-send message so the invitee knows why they got the link.
    const message = t('bravosInviteShareText', { event: event.name });
    try {
      if (navigator.share) {
        await navigator.share({ title: event.name, text: message, url: link });
      } else {
        await navigator.clipboard.writeText(`${message}\n${link}`);
        setInviteCopied(true);
        setTimeout(() => setInviteCopied(false), 2000);
      }
    } catch { /* user cancelled the share sheet, or clipboard blocked — no-op */ }
  };

  const sendInviteEmail = () => {
    if (busyInvite || !guest.email.trim()) return;
    setBusyInvite(true);
    setInviteNotice(null);
    apiRequest('POST', '/bravos/lab-invite', { event_id: eventId, guests: [guest] })
      .then(() => {
        setInviteNotice(t('inviteEmailSent', { name: guest.first_name || guest.email }));
        setGuest({ first_name: '', last_name: '', email: '' });
      })
      .catch((err) => setInviteNotice(t(err.message, { defaultValue: t('somethingWentWrong') })))
      .finally(() => setBusyInvite(false));
  };

  // ★ A contact person is coming to the Lab too, and the way to their own seat used to run
  // through the member home and a view switch — three screens away from the page that made
  // them want to come. A seat still open in a pool they already hold is claimed right here
  // (their own first, then the company's, as the endpoint picks); with none open the button
  // hands over to the very checkout a member gets, so nobody is asked to pay for a free seat.
  const registerMyself = () => {
    if (busySelf) return;
    if (!data.bravos.self_claimable) {
      navigate(data.bravos.is_member || !data.bravos.storefront_slug
        ? `/book/${eventId}`
        : `/${data.bravos.storefront_slug}`);
      return;
    }
    setBusySelf(true);
    setSelfNotice(null);
    apiRequest('POST', '/bravos/claim-seat', { event_id: eventId })
      .then(() => { setSelfDone(true); setSelfNotice(t('seatClaimed')); })
      .catch((err) => setSelfNotice(t(err.message, { defaultValue: t('claimFailed') })))
      .finally(() => setBusySelf(false));
  };

  // Date / time (from–to) / location chips — reused above the banner hero and below the
  // square Lab poster (the class differs: light-on-image vs dark-on-page).
  const renderChips = (chipClass) => (
    <>
      {event.date && (
        <span className={chipClass}>
          <Icon name="calendar-lines" />
          {dateLabel}
        </span>
      )}
      {timeLabel && (
        <span className={chipClass}>
          <Icon name="clock" />
          {timeLabel}
        </span>
      )}
      {event.location_name && (
        <span className={chipClass}>
          <Icon name="marker" />
          {event.location_name}
        </span>
      )}
      {/* Somebody ELSE runs this one. Said on the same line as the venue and the hour, because
          it is the fact that changes how all three read — and where they gave us the organiser's
          page, the chip IS that link, since the programme and the price are over there and none
          of it is ours to retype. Without a link it still says so, plainly. */}
      {event.is_external_event && (
        event.external_url ? (
          <a className={`${chipClass} pe-chip-link`} href={event.external_url}
             target="_blank" rel="noreferrer">
            <Icon name="link" />
            {t('externalEventPage')}
          </a>
        ) : (
          <span className={chipClass}>
            <Icon name="link" />
            {t('externalEvent')}
          </span>
        )
      )}
      {/* Put the date in your own calendar, from the line that states it. It used to hang off
          the planning header, which a Bravos Lab has no rows for — so the one page most likely
          to be read on a phone was the one page without the button. */}
      {event.date && eventIcsUrl(event) && (
        <a className={`${chipClass} pe-chip-link`} href={eventIcsUrl(event)}>
          <Icon name="calendar-plus" />
          {t('addToCalendar')}
        </a>
      )}
    </>
  );

  // A private event that asks the question itself owns the answer.
  // ★ An invite-only event that this viewer is not on the guest list for shows the line
  // instead of the buttons. The server refuses the POST anyway (set_rsvp), so this is the
  // screen agreeing with the route rather than the guard itself — a button that 400s is worse
  // than no button.
  const notInvited = !!(data.company_event && data.company_event.is_invite_only
    && data.company_event.i_am_invited === false);
  // ★ A CLUB's members-only event: every colleague sees it, only a member may register — so a
  // non-member gets the way into the club where the buttons would have been (the teaser).
  const clubOnly = !!(data.company_event && data.company_event.members_only
    && data.company_event.i_am_member === false);
  const hasRsvp = !!(data.company_event && data.company_event.registration_enabled
    && !data.company_event.is_cancelled) && !notInvited && !clubOnly;

  // What the Bravos block says, if anything: invite (a contact person), the registered
  // banner, or the way to a ticket. Registered AND an RSVP pair on the same event is the same
  // answer stated twice — and after "I can't make it", a banner reading "you're registered"
  // contradicts the answer you just gave. Where the RSVP is on, it owns the state and the
  // banner stands down.
  const bravosMode = !data.bravos ? null
    : data.bravos.is_contact ? 'invite'
      : data.bravos.registered ? (hasRsvp ? null : 'registered')
        : (data.bravos.has_shop && data.bravos.storefront_slug) ? 'buy' : null;

  // The blocks that answer "so what do I do with this?" — a private event's RSVP, the Bravos
  // CTA, and last the owner's own shortcuts: the page asks the reader whether they are coming
  // before it offers the admin the editor. A square-poster event puts them BESIDE the visual, a
  // banner event under it, so they are defined once here rather than written out in both.
  const actionBlocks = (
    <>
      {/* ============== NETWORKING ============== */}
      {/* "Show my QR", first of the actions and therefore at the top of the column beside the
          poster (.pe-lab-hero__aside renders these as one wrapping row of pills). It used to sit
          near the foot of the page, under the description and the map — which is the wrong end
          of a page somebody opens while standing in the room with the person they want to swap
          details with.

          ★ Deliberately the same pill as the "Before we meet" link under it (`pe-bravos-alt`)
          rather than the full-width brand-filled bar it used to be: in the aside every control is
          normalised to one pill size (PublicEvent.css), and a full-width dark bar in that row
          read as a different family of thing. The two belong to the same day and now look it.

          The server decides whether this draws at all: `networking` is null on every event
          without the feature, and can_show is false for a visitor who is not taking part. */}
      {data.networking && data.networking.can_show && (
        <section className="pp-section">
          <button
            type="button"
            className="pe-bravos-alt"
            onClick={() => setShowMeetQr(true)}
          >
            <Icon name="user-add" /> <span>{t('meetShowQr')}</span>
          </button>
        </section>
      )}
      {/* A PRIVATE company event: I'm coming / I'm not coming, right where the invitation
          mail's button lands. The second one is the point — every tool can record a yes. */}
      {hasRsvp && (
        <section className="pp-section">
          {/* Neither answer is pre-selected: until you press one, both buttons are outlined and
              read the same, so a filled button always means "this is what you answered" and never
              "this is the default". Once answered, the chosen side colours — green for yes, red
              for no — and the other one becomes the way back out of that answer.
              The two answers and the headcount are one group: a stack on a phone, a single
              wrapping row of compact controls beside the poster on a desktop. The count reads
              last, at the end of the answers rather than above them. */}
          <div className="pe-action-row">
            <button
              type="button"
              className={rsvp === 'going' ? 'pe-rsvp-yes' : 'pe-bravos-alt'}
              onClick={() => answerRsvp('going')}
              disabled={busyRsvp}
            >
              <Icon name="check" /> {comingLabel}
            </button>
            <button
              type="button"
              className={rsvp === 'not_going' ? 'pe-rsvp-no' : 'pe-bravos-alt'}
              onClick={() => answerRsvp('not_going')}
              disabled={busyRsvp}
            >
              <Icon name="cross-circle" /> {notComingLabel}
            </button>
            {data.company_event.going != null && (
              <span className="pe-going-count">
                {t('goingCount', { n: data.company_event.going })}
              </span>
            )}
          </div>
          {/* The link only appears once you've said you're coming — an online event's joining
              link on a page anyone in the company can open is a link that leaks. */}
          {rsvp === 'going' && data.company_event.is_online
            && data.company_event.meeting_url && (
            <a className="pe-bravos-alt pe-chip-link"
               href={data.company_event.meeting_url}
               target="_blank" rel="noreferrer">
              <Icon name="link" /> {t('meetingLink')}
            </a>
          )}
          {rsvpNotice && <p className="bravos-cal__notice">{rsvpNotice}</p>}
        </section>
      )}
      {/* ★ What "before the event" MEANS. A `pre` survey was a coloured pill on the host screen
          and nothing else — nothing anywhere asked anybody. It appears the second somebody is
          counted as coming, and stays until they have answered it.

          ★ OUTSIDE the RSVP section, which only draws on a private event. A Bravos Lab has no
          invitation to answer — you buy a seat or claim one — so the questions attached to it
          were rendered nowhere at all. The list itself is the gate: the server sends surveys
          only to somebody it already counts as coming (`company_event.pending_surveys` for a
          private event, `my_surveys` for a public one), so there is nothing left to test here. */}
      {pending.length > 0 && (
        <section className="pp-section">
          <div className="pe-survey-ask">
            <p className="pe-survey-ask__title">
              <Icon name="comment-question" />{' '}
              {pending[0].phase === 'post' ? t('surveyAfterTitle') : t('surveyBeforeTitle')}
            </p>
            {pending.map((s) => (
              <a key={s.id} className="pe-bravos-alt pe-chip-link" href={s.link}>
                <Icon name="document" /> {s.name || t('answerIt')}
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Where the answer buttons would have been, for somebody who reached this page by
          having ATTENDED rather than by being on the guest list. It says why there is nothing
          to press, instead of leaving a silent gap. */}
      {clubOnly && data.company_event.club && (
        <section className="pe-section">
          <p className="pe-invite-only">
            <Icon name="users" /> {t('clubJoinToRegister')}
          </p>
          <button type="button" className="bravos-submit bravos-submit--ghost"
                  onClick={() => navigate(`/clubs/${data.company_event.club.id}`)}>
            {data.company_event.club.name} <Icon name="angle-right" />
          </button>
        </section>
      )}

      {notInvited && (
        <section className="pe-section">
          <p className="pe-invite-only">
            <Icon name="lock" /> {t('inviteOnlyNotYou')}
          </p>
        </section>
      )}

      {/* BRAVOS — a contact person INVITES a member; others register / see "registered" */}
      {bravosMode && (
        <section className="pp-section">
          {bravosMode === 'invite' ? (
            <>
              {/* The three ways to fill a Lab stay one group; beside the poster on a desktop
                  they become one wrapping row (see .pe-action-row), and the guest form opens
                  underneath the row rather than in the middle of it. */}
              <div className="pe-action-row">
                <button
                  type="button"
                  className="pe-bravos-cta"
                  style={data.bravos.primary_color
                    ? { backgroundColor: data.bravos.primary_color } : undefined}
                  onClick={() => navigate('/invite-to-lab', { state: { eventId } })}
                >
                  <Icon name="users" /> {t('inviteMembers')}
                </button>

                <button type="button" className="pe-bravos-alt"
                        onClick={() => setShowGuest((v) => !v)}>
                  <Icon name="envelope" /> {t('sendInviteEmail')}
                </button>

                <button type="button" className="pe-bravos-alt" onClick={sendInviteLink}>
                  <Icon name={inviteCopied ? 'check' : 'link'} />
                  {inviteCopied ? t('copied') : t('sendInviteLink')}
                </button>

                {/* Filling the Lab and being in it are two different jobs; the second one keeps
                    its place at the end of the row. It stands down once there is nothing to
                    press: already in, or no seat to take and nothing to buy. */}
                {(data.bravos.self_registered || selfDone) ? (
                  <span className="pe-bravos-registered">
                    <Icon name="check" /> {t('youAreRegistered')}
                  </span>
                ) : (data.bravos.self_claimable || data.bravos.has_shop) ? (
                  <button type="button" className="pe-bravos-alt"
                          onClick={registerMyself} disabled={busySelf}>
                    <Icon name="ticket" />
                    {busySelf ? t('pleaseWait') : t('registerMyself')}
                  </button>
                ) : null}
              </div>

              {showGuest && (
                <div className="bravos-form">
                  <div className="bravos-form__row">
                    <input type="text" value={guest.first_name} placeholder={t('firstName')}
                           onChange={(e) => setGuest({ ...guest, first_name: e.target.value })} />
                    <input type="text" value={guest.last_name} placeholder={t('lastName')}
                           onChange={(e) => setGuest({ ...guest, last_name: e.target.value })} />
                  </div>
                  <input type="email" value={guest.email} placeholder={t('enterEmail')}
                         onChange={(e) => setGuest({ ...guest, email: e.target.value })} />
                  <button type="button" className="pe-bravos-alt"
                          onClick={sendInviteEmail}
                          disabled={busyInvite || !guest.email.trim()}>
                    {busyInvite ? t('pleaseWait') : t('send')}
                  </button>
                </div>
              )}

              {inviteNotice && <p className="bravos-cal__notice">{inviteNotice}</p>}
              {selfNotice && <p className="bravos-cal__notice">{selfNotice}</p>}
            </>
          ) : bravosMode === 'registered' ? (
            <div className="pe-bravos-registered">
              <Icon name="check" /> {t('youAreRegistered')}
            </div>
          ) : (
            <div className="pe-action-row">
              <button
                type="button"
                className="pe-bravos-cta"
                style={data.bravos.primary_color
                  ? { backgroundColor: data.bravos.primary_color } : undefined}
                onClick={() => navigate(data.bravos.is_member
                  ? `/book/${eventId}`
                  : `/${data.bravos.storefront_slug}`)}
              >
                <Icon name="ticket" /> {data.bravos.is_member ? t('bookNow') : t('getYourTicket')}
              </button>
            </div>
          )}
        </section>
      )}

      {/* THE OWNER'S own way in — two doors, two gates. A company admin reading their event's
          public page is one click from the editor instead of walking back through the programme
          to find it, and whoever runs the event is one click from the host screen. Both flags
          are the server's answer to those screens' own gates, so neither ever links into a
          403 — see get_public_event. */}
      {(data.can_edit || data.can_run) && (
        <div className="pe-owner-actions">
          {data.can_edit && (
            <Link to={`/company-event/${eventId}/edit`} className="pe-owner-btn">
              <Icon name="settings" /> {t('editEvent')}
            </Link>
          )}
          {/* Running it on the day is the other half, and its own gate — the narrower one:
              an external host we sent in gets the editor above and NOT this. `can_run` is the
              host screen's own answer, so this never links into a 403 either. */}
          {data.can_run && (
            <Link to={`/host-event/${eventId}`} className="pe-owner-btn">
              <Icon name="megaphone" /> {t('hostEvent')}
            </Link>
          )}
        </div>
      )}

      {/* PART OF A LEAGUE — a day that belongs to a competition running across evenings says so
          here, under the owner's doors, and is one press from the league itself. Its own line
          rather than a chip beside the date: it is not a fact ABOUT this evening, it is the way
          out of it. The server sends it only for a DAY (never for the league's own master event)
          and picks the destination per reader — the admin screen for whoever runs the league,
          the league's own event page for everybody else. See get_public_event. */}
      {data.league && (
        <div className="pe-league">
          <Link
            to={data.league.can_manage
              ? `/league/${data.league.id}`
              : `/public-event/${data.league.master_event_id}`}
            className="pe-league__link"
          >
            <Icon name="leaderboard" className="pe-league__icon" />
            <span className="pe-league__body">
              <span className="pe-league__label">{t('partOfLeague')}</span>
              <span className="pe-league__name">{data.league.name}</span>
            </span>
            <Icon name="angle-right" className="pe-league__chev" />
          </Link>
        </div>
      )}

      {/* A COMPETITION DAY has a board, and until now a colleague had no way at all to it — the
          organiser's ranking and the beamer board are both host-gated, and /leaderboard needs an
          event+team context a Bravos member's token does not carry. Same shape as the league line
          above, because it is the same kind of thing: not a fact about the evening, a way out of
          it.

          ★ `has_standings`, decided by the server, and not a date comparison here: it means "the
          day has begun", which includes today — the one day everybody is refreshing it — and the
          same flag drives the same button on /my-competition, so the two cannot drift apart by an
          afternoon. Signed-out readers get nothing: the standing of a company's internal
          competition is not public. */}
      {data.has_standings && data.is_authenticated && (
        <div className="pe-league">
          <Link to={`/company-event/${eventId}/standings`} className="pe-league__link">
            <Icon name="trophy" className="pe-league__icon" />
            <span className="pe-league__body">
              <span className="pe-league__label">{t('tournamentTitle')}</span>
              <span className="pe-league__name">{t('seeTheStandings')}</span>
            </span>
            <Icon name="angle-right" className="pe-league__chev" />
          </Link>
        </div>
      )}
    </>
  );

  // COUNTDOWN — pre-event only. Its own block rather than the tail of `actionBlocks`: four
  // dials are a band across the page, not a control in the column beside the poster, so the
  // square-poster layout gives it a full-width row of its own under both columns.
  const countdownBlock = isPreEvent && (
    <div className="pe-countdown">
      <div className="pe-countdown__inner">
        <div className="pe-countdown__label">
          {t('upcoming', { defaultValue: 'Starts in' })}
        </div>
        <Countdown countdownDate={event.date} />
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="pp-page">

        {/* HEADER (back arrow, no title — title lives in the hero, so the bar is
            "bare" and has to reserve the arrow's own height) */}
        <div className="el-topbar el-topbar--bare">
          <button type="button" className="el-back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <Icon name="angle-left" />
          </button>
        </div>

        {/* HERO — a Bravos Lab and a company's own event both show their visual as a square
            poster (clean, no logo/name over it) with the chips below; every other event keeps
            the wide banner hero with overlay. Either way the visual opens full-screen on tap:
            a poster is small on a phone and the detail on it (the programme, the address, who
            is speaking) is unreadable at that size. */}
        {isSquareHero ? (
          <section className="pe-lab-hero">
            <button
              type="button"
              className="pe-poster-btn"
              onClick={() => setZoom(true)}
              aria-label={t('enlargeImage')}
            >
              <img className="pe-lab-hero__poster" src={heroBg} alt={event.name} />
              <span className="pe-zoom-badge"><Icon name="expand" /></span>
            </button>
            {/* Everything the poster does not itself say. From 1024px up this sits BESIDE the
                poster instead of under it (PublicEvent.css): a 20rem square in an 820px column
                left a band of empty page down both sides and pushed the date, the pitch and the
                ticket button below the fold. On a phone the column IS the page, so it stacks. */}
            <div className="pe-lab-hero__aside">
              <h1 className="pe-lab-hero__name">{event.name}</h1>
              {/* The one-liner reads as the title's subtitle — set bold, straight under the name
                  and above the facts, so what the Lab is about is the second thing you read. It
                  therefore loses the card it has further down the page, and that section (which
                  would repeat it) stands down for a square-poster event. */}
              {event.short_desc && (
                <div
                  className="pe-lab-hero__tagline"
                  dangerouslySetInnerHTML={{ __html: event.short_desc }}
                />
              )}
              <div className="pe-lab-hero__chips">{renderChips('pe-lab-hero__chip')}</div>
              {actionBlocks}
            </div>
            {countdownBlock}
          </section>
        ) : (
          <section className="pe-hero">
            <div className="pe-hero__bg" style={{ backgroundImage: `url(${heroBg})` }} />
            <div className="pe-hero__shade" />
            {/* The banner itself opens full-screen. The layer sits UNDER the title and the
                chips (no z-index of its own), so "add to calendar" keeps its own tap. */}
            <button
              type="button"
              className="pe-hero__zoom"
              onClick={() => setZoom(true)}
              aria-label={t('enlargeImage')}
            >
              <span className="pe-zoom-badge"><Icon name="expand" /></span>
            </button>

            <div className="pe-hero__inner">
              <div className="pe-hero__logo-ring">
                <img src={logo} alt="" className="pe-hero__logo" />
              </div>
              <h1 className="pe-hero__name">{event.name}</h1>

              <div className="pe-hero__chips">{renderChips('pe-hero__chip')}</div>
            </div>
          </section>
        )}

        {/* THE VISUAL, FULL-SCREEN — tap anywhere (or Escape) to close. */}
        {zoom && (
          <div className="pe-lightbox" onClick={() => setZoom(false)} role="presentation">
            <img className="pe-lightbox__img" src={heroBg} alt={event.name} />
            <button type="button" className="pe-lightbox__close" aria-label={t('close')}>
              <Icon name="cross-small" />
            </button>
          </div>
        )}

        {/* The actions live in the hero's own column when the visual is a square poster;
            a banner event shows them here, under the hero, where they always were. */}
        {!isSquareHero && (
          <>
            {actionBlocks}
            {countdownBlock}
          </>
        )}

        {/* HOST(S) — small photo chip → the host's public screen */}
        {hosts.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('hostedBy')}</h2>
            </header>
            <div className="pe-hosts">
              {hosts.map((h) => (
                <button
                  key={h.partner_id}
                  type="button"
                  className="pe-host"
                  onClick={() => navigate(`/host/${h.partner_id}`)}
                >
                  <span
                    className="pe-host__photo"
                    style={{ backgroundImage: `url("${h.photo_url || logo}")` }}
                  />
                  <span className="pe-host__body">
                    <span className="pe-host__name">
                      {[h.first_name, h.name].filter(Boolean).join(' ')}
                    </span>
                    {/* A host who is also a listed specialist shows their headline here; the
                        host page then carries the full specialist block. */}
                    {h.headline && <span className="pe-host__headline">{h.headline}</span>}
                  </span>
                  <Icon name="angle-right" className="pe-host__chev" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* PHOTOALBUM — badge to the event's public album (if it has one).

            ★ Directly under the hosts, not down beside the social wall where it used to sit. The
            album is the one thing a visitor who missed the event came to see, and it was below
            the description, the planning, the map and the wall — four screens of scrolling past
            everything they were NOT looking for. */}
        {event.has_album && (
          <section className="pp-section">
            <button
              type="button"
              className="pe-photos-badge"
              onClick={() => navigate(`/public-album/${eventId}`)}
            >
              <Icon name="images" /> <span>{t('viewPhotoAlbum')}</span>
              <Icon name="angle-right" className="pe-photos-badge__chev" />
            </button>
          </section>
        )}

        {/* SHORT DESC — a square-poster event already carries it as the poster's subtitle */}
        {!isSquareHero && event.short_desc && (
          <section className="pp-section">
            <div
              className="pe-prose"
              dangerouslySetInnerHTML={{ __html: event.short_desc }}
            />
          </section>
        )}

        {/* LONG DESC */}
        {event.long_desc && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('description', { defaultValue: 'About' })}</h2>
            </header>
            <div
              className="pe-prose"
              dangerouslySetInnerHTML={{ __html: event.long_desc }}
            />
          </section>
        )}

        {/* PLANNING */}
        {hasPlanning && (
          <section className="pp-section">
            <header className="pp-section-header pe-planning-header">
              <h2 className="pp-section-title">{t('planning')}</h2>
            </header>

            <div className="pe-planning">
              {planningEntries.map(([dayIso, items]) => {
                const showDayHeader = dayIso !== '1900-01-01';
                const pretty = showDayHeader
                  ? new Date(dayIso).toLocaleDateString(undefined, {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                    })
                  : null;

                return (
                  <div key={dayIso} className="pe-day">
                    {showDayHeader && <div className="pe-day__label">{pretty}</div>}
                    {items.map((activity, index) => {
                      const time = activity.start_time
                        ? `${toHHMM(activity.start_time)}${activity.end_time ? `–${toHHMM(activity.end_time)}` : ''}`
                        : '';
                      return (
                        <div className="pe-slot" key={`${dayIso}-${index}`}>
                          <div className="pe-slot__time">{time}</div>
                          <div className="pe-slot__name">
                            {activity.name || t('battleNum', { number: activity.sport_order })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* LOCATION — practical notes + the Google map embed */}
        {(event.location_embed || event.location_desc) && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('location')}</h2>
            </header>
            {event.location_desc && (
              <p className="pe-location-note">{event.location_desc}</p>
            )}
            {event.location_embed && (
              <div
                className="pe-map"
                dangerouslySetInnerHTML={{ __html: event.location_embed }}
              />
            )}
          </section>
        )}

        {/* THE SOCIAL LAYER — photos taken by the people here, the wall, the host's recap.
            It draws itself or nothing at all: the component asks the server, and a viewer who was
            not at this event (or an event whose brand does not have the feature) gets a status
            code back and the section never appears.

            ★ `signedIn` so a stranger's browser never makes that request in the first place. It
            is a @token_required endpoint, and a 401 on a GET is how this app recognises an expired
            session — so on a PUBLIC event page the wall was quietly logging visitors out and
            sending them to /login. utils.apiRequest now needs the "you were signed in" marker
            before it does that; this stops the pointless request as well. */}
        <section className="pp-section">
          <EventSocial eventId={eventId} signedIn={!!data.is_authenticated} />
        </section>

        {/* ...and for the visitor who is NOT signed in, one line saying what they are missing.
            Only on a Bravos event: it is the only brand with a wall behind the door, and an
            invitation to sign in for something that does not exist is worse than silence. */}
        {!data.is_authenticated && data.bravos && data.bravos.is_bravos && (
          <section className="pp-section">
            <div className="pe-signin-nudge">
              <p className="pe-signin-nudge__title">
                <Icon name="camera" /> {t('socialSignInTitle')}
              </p>
              <p className="pe-signin-nudge__note">{t('socialSignInNote')}</p>
              <Link to="/login" className="pe-bravos-alt pe-chip-link">
                <Icon name="sign-in-alt" /> {t('login')}
              </Link>
            </div>
          </section>
        )}

        {/* The QR itself now sits at the top of the actions, beside the poster — see the
            NETWORKING block in `actionBlocks`. Only the sheet it opens stays here, at page
            level: a full-screen overlay does not belong inside the hero's own column. */}
        {showMeetQr && (
          <MeetQrSheet eventId={eventId} onClose={() => setShowMeetQr(false)} />
        )}

        {/* VENUE MAP */}
        {event.venue_map && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('venueMap', { defaultValue: 'Venue map' })}</h2>
            </header>
            <a href={event.venue_map} target="_blank" rel="noreferrer" className="pe-venue-image">
              <img src={event.venue_map} alt={t('venueMap', { defaultValue: 'Venue map' })} />
            </a>
          </section>
        )}

        {/* SEATING / TABLE MAP */}
        {event.seating_map && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('seatingMap', { defaultValue: 'Seating map' })}</h2>
            </header>
            <a href={event.seating_map} target="_blank" rel="noreferrer" className="pe-venue-image">
              <img src={event.seating_map} alt={t('seatingMap', { defaultValue: 'Seating map' })} />
            </a>
          </section>
        )}

        {/* SPONSORS */}
        {sponsors.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('sponsoredBy')}</h2>
            </header>
            <div className="pe-sponsors">
              {/* One column per sponsor type, most important first (the API
                  orders them), each cut off from the next by a rule; untyped
                  sponsors trail as a last, unlabelled column. The wall clips
                  the row's overhanging left edge so the rule vanishes on the
                  first column of every wrapped line -- see the stylesheet. */}
              <div className="pe-sponsors__wall">
                <div className="pe-sponsors__row">
                  {groupSponsorsByType(sponsors).map((group) => (
                    <div className="pe-sponsors__group" key={group.type || '_untyped'}>
                      {group.type && (
                        <div className="pe-sponsors__type">{group.type}</div>
                      )}
                      <div className="pe-sponsors__grid">
                        {group.items.map((s) => (
                          // A sponsor with its own URL links out; the rest keep the
                          // internal public-company page. The key carries the type too:
                          // a sponsor tagged with several types appears in each group.
                          s.url ? (
                            <a
                              key={`${group.type || ''}-${s.id}`}
                              href={s.url}
                              target="_blank"
                              rel="noreferrer"
                              className="pe-sponsors__item"
                              style={{ backgroundImage: `url(${s.photo_url || eventFallback})` }}
                              aria-label={s.name || ''}
                            />
                          ) : (
                            <Link
                              key={`${group.type || ''}-${s.id}`}
                              to={`/public-company/${s.id}`}
                              className="pe-sponsors__item"
                              style={{ backgroundImage: `url(${s.photo_url || eventFallback})` }}
                              aria-label={s.name || ''}
                            />
                          )
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

      </div>
    </Layout>
  );
};

export default Page;
